// RBAC Management UI
// Phase 6 - Part 2: Role-Based Access Control Management

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tabs,
  Tab,
  Alert,
  Pagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import SecurityIcon from '@mui/icons-material/Security';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';

const ROLE_COLORS = {
  ADMIN: 'error',
  HR_MANAGER: 'primary',
  HR_STAFF: 'info',
  EMPLOYEE: 'default',
  POS_MANAGER: 'secondary',
  CASHIER: 'warning',
};

const ROLE_DESCRIPTIONS = {
  ADMIN: 'Full system access',
  HR_MANAGER: 'Full HRM access with approval rights',
  HR_STAFF: 'Basic HRM operations',
  EMPLOYEE: 'Self-service access only',
  POS_MANAGER: 'Full POS access',
  CASHIER: 'Basic POS operations',
};

export default function RBACManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [permissionOverrides, setPermissionOverrides] = useState({});
  const [effectivePermissions, setEffectivePermissions] = useState([]);
  
  const [tabValue, setTabValue] = useState(0);
  const [alert, setAlert] = useState({ show: false, type: 'success', message: '' });

  const [availableRoles, setAvailableRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState({});

  useEffect(() => {
    fetchUsers();
    fetchPermissions();
  }, [pagination.page, search, roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });
      if (search) params.append('search', search);
      if (roleFilter) params.append('role', roleFilter);

      const { data } = await axios.get(`/api/rbac/users?${params.toString()}`);
      if (data.success) {
        setUsers(data.data || []);
        setPagination(data.pagination || pagination);
      }
    } catch (error) {
      showAlert('error', 'Failed to fetch users: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const { data } = await axios.get('/api/rbac/permissions');
      if (data.success) {
        setAvailablePermissions(data.data.permissions || {});
        setAvailableRoles(Object.keys(data.data.roleTemplates || {}));
      }
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    }
  };

  const handleEditUser = (user) => {
    console.log('👤 Opening edit dialog for user:', user.staff_name);
    console.log('📋 User permission overrides from server:', user.permissionOverrides);
    
    setSelectedUser(user);
    setSelectedRoles(user.roles || []);
    setPermissionOverrides(user.permissionOverrides || {});
    setEffectivePermissions(user.effectivePermissions || []);
    setEditDialogOpen(true);
  };

  const handleRoleToggle = (role) => {
    setSelectedRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  const handlePermissionOverride = (permission, value) => {
    const newValue = value === 'default' ? undefined : value === 'allow';
    console.log(`🔧 Setting override: ${permission} = ${value} → ${newValue}`);
    
    setPermissionOverrides(prev => {
      const updated = {
        ...prev,
        [permission]: newValue,
      };
      console.log('📝 Updated overrides state:', updated);
      return updated;
    });
  };

  const handleSaveRoles = async () => {
    try {
      const { data } = await axios.put(`/api/rbac/user-roles/${selectedUser._id}`, {
        roles: selectedRoles,
      });
      
      if (data.success) {
        showAlert('success', 'User roles updated successfully');
        fetchUsers();
        setEditDialogOpen(false);
      }
    } catch (error) {
      showAlert('error', 'Failed to update roles: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSavePermissions = async () => {
    try {
      // Filter out undefined values (keep both true and false)
      const cleanOverrides = Object.fromEntries(
        Object.entries(permissionOverrides).filter(([_, v]) => v !== undefined)
      );

      console.log('🔍 Permission Overrides State:', permissionOverrides);
      console.log('🧹 Clean Overrides (after filter):', cleanOverrides);
      console.log('📊 Override count - Before:', Object.keys(permissionOverrides).length, 'After:', Object.keys(cleanOverrides).length);
      
      // Show all overrides in a readable format
      console.table(cleanOverrides);
      console.log('📋 All permissions being saved:', Object.keys(cleanOverrides).join(', '));

      const { data } = await axios.put(`/api/rbac/user-permission-overrides/${selectedUser._id}`, {
        permissionOverrides: cleanOverrides,
      });
      
      console.log('✅ Server response:', data);
      
      if (data.success) {
        showAlert('success', 'Permission overrides updated successfully');
        fetchUsers();
        setEditDialogOpen(false);
      }
    } catch (error) {
      console.error('❌ Save error:', error);
      showAlert('error', 'Failed to update permissions: ' + (error.response?.data?.message || error.message));
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
  };

  const handleInitializeRoles = async () => {
    if (!window.confirm('This will set default "EMPLOYEE" role for all users who don\'t have roles assigned. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post('/api/rbac/initialize-roles');
      console.log('🔄 Initialize Roles Response:', data);
      
      if (data.success) {
        console.log(`✅ Initialized roles for ${data.data?.modifiedCount || 0} users`);
        showAlert('success', data.message);
        fetchUsers(); // Refresh the list
      } else {
        console.warn('⚠️ Initialize roles returned success: false');
        showAlert('warning', data.message || 'No users needed role initialization');
      }
    } catch (error) {
      console.error('❌ Initialize roles error:', error);
      showAlert('error', 'Failed to initialize roles: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUsers();
  };

  const groupPermissionsByModule = () => {
    const grouped = {};
    Object.entries(availablePermissions).forEach(([perm, desc]) => {
      const module = perm.split('.')[0];
      if (!grouped[module]) grouped[module] = [];
      grouped[module].push({ permission: perm, description: desc });
    });
    return grouped;
  };

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <SecurityIcon color="primary" />
        <Typography variant="h5">RBAC Management</Typography>
      </Box>

      {alert.show && (
        <Alert severity={alert.type} onClose={() => setAlert({ show: false, type: '', message: '' })} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Users"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, username, or email"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filter by Role</InputLabel>
              <Select
                value={roleFilter}
                label="Filter by Role"
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <MenuItem value="">All Roles</MenuItem>
                {availableRoles.map(role => (
                  <MenuItem key={role} value={role}>{role}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={loading}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              startIcon={<RefreshIcon />}
              onClick={handleInitializeRoles}
              disabled={loading}
            >
              Initialize Roles
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell>Permissions Count</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(user => (
              <TableRow key={user._id} hover>
                <TableCell>{user.staff_name || user.username}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.department?.departmentName || '-'}</TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {(user.roles || []).map(role => (
                      <Chip
                        key={role}
                        label={role}
                        size="small"
                        color={ROLE_COLORS[role] || 'default'}
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>{user.effectivePermissions?.length || 0}</TableCell>
                <TableCell>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditUser(user)}
                  >
                    Manage
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {loading ? 'Loading...' : 'No users found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Pagination
            color="primary"
            page={pagination.page}
            count={pagination.pages}
            onChange={(_, page) => setPagination(prev => ({ ...prev, page }))}
          />
        </Box>
      </Paper>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Manage Access - {selectedUser?.staff_name || selectedUser?.username}
        </DialogTitle>
        <DialogContent dividers>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
            <Tab label="Roles" />
            <Tab label="Permission Overrides" />
            <Tab label="Effective Permissions" />
          </Tabs>

          {/* Roles Tab */}
          {tabValue === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Select one or more roles for this user. Roles provide a set of default permissions.
              </Typography>
              <FormGroup>
                {availableRoles.map(role => (
                  <FormControlLabel
                    key={role}
                    control={
                      <Checkbox
                        checked={selectedRoles.includes(role)}
                        onChange={() => handleRoleToggle(role)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1">{role}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ROLE_DESCRIPTIONS[role] || ''}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </Box>
          )}

          {/* Permission Overrides Tab */}
          {tabValue === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Override specific permissions for this user. These override the permissions from roles.
              </Typography>
              {Object.entries(groupPermissionsByModule()).map(([module, perms]) => (
                <Box key={module} mb={2}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {module}
                  </Typography>
                  {perms.map(({ permission, description }) => (
                    <Box key={permission} display="flex" alignItems="center" gap={2} mb={1}>
                      <Typography variant="body2" sx={{ flex: 1, fontSize: '0.85rem' }}>
                        {permission}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {description}
                        </Typography>
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                          value={
                            permissionOverrides[permission] === undefined
                              ? 'default'
                              : permissionOverrides[permission]
                              ? 'allow'
                              : 'deny'
                          }
                          onChange={(e) => handlePermissionOverride(permission, e.target.value)}
                        >
                          <MenuItem value="default">Default</MenuItem>
                          <MenuItem value="allow">Allow</MenuItem>
                          <MenuItem value="deny">Deny</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          )}

          {/* Effective Permissions Tab */}
          {tabValue === 2 && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                These are the final permissions this user has after applying roles and overrides.
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={2}>
                {effectivePermissions.map(perm => (
                  <Chip key={perm} label={perm} size="small" />
                ))}
                {effectivePermissions.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No permissions assigned
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          {tabValue === 0 && (
            <Button variant="contained" onClick={handleSaveRoles}>
              Save Roles
            </Button>
          )}
          {tabValue === 1 && (
            <Button variant="contained" onClick={handleSavePermissions}>
              Save Overrides
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
